import snakeCase from 'lodash/snakeCase'

const googleAnalytics3 = (...args) => {
  // We don't want to send any analytics events during the turbo in memory cache loading time.
  // We wait a few more milliseconds for the network request to load, then we allow events to fire.
  if (document.documentElement.hasAttribute('data-turbo-preview')) return

  if (typeof window !== 'undefined' && typeof window.gtag3 === 'function') {
    window.gtag3(...args)
  }
}

const googleAnalytics3Event = ({ eventAction, eventCategory, eventLabel, eventValue = null, nonInteraction = false, callback = null }) => {
  // Firefox and ad blockers will prevent gtag from running at all.
  // So we register a callback that is fired after 500ms in case gtag doesn't run at all.
  const eventCallbackFallback = callback ? setTimeout(callback, 500) : null

  // Extract the current pageType and itemType from the <body> tag, append it to every GA event we send:
  const pageType = document.body.dataset.analyticsPageType
  const itemType = document.body.dataset.analyticsItemType

  // Push the GA event into Google Tag Manager:
  googleAnalytics3(
    'event',
    eventAction,
    {
      event_category: eventCategory,
      event_label: eventLabel,
      value: eventValue,
      non_interaction: nonInteraction,
      event_callback: (containerId) => {
        // This callback is fired for every "tag" that is registered in Google Tag Manager.
        // e.g. once for Analytics, and once for Optimize.
        // We have to check the container ID starts with "UA-" to know it's a Google Analytics callback.
        if (containerId?.startsWith('UA-')) {
          // This fires when the GA event has successfully been recorded.
          // We remove our fallback timer and call the callback function if one exists
          clearTimeout(eventCallbackFallback)
          callback && callback()
        }
      },
      // see "custom_map" in gtag_config for these custom dimension mappings:
      page_type: pageType,
      item_type: itemType
    }
  )
}

const googleAnalytics = (eventData) => {
  // We don't want to send any analytics events during the turbo in memory cache loading time.
  // We wait a few more milliseconds for the network request to load, then we allow events to fire.
  if (document.documentElement.hasAttribute('data-turbo-preview')) return

  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(eventData)
  }
}

const googleAnalyticsEvent = ({ eventName, eventType = 'user', isECommerceEvent = false, callback = null, ...eventAttributes }) => {
  // Firefox and ad blockers will prevent GTM from running at all.
  // So we register a callback that is fired after 500ms in case GTM never gets to process the event.
  const eventCallbackFallback = callback ? setTimeout(callback, 500) : null

  const event = isECommerceEvent ? 'e-commerce' : 'track_event'

  const eventData = {
    event,
    event_name: eventName,
    event_type: eventType,
    eventCallback: (containerId) => {
      // This callback is fired for every "tag" that is registered in Google Tag Manager.
      // e.g. once for Analytics, and once for Optimize.
      // We have to check the container ID starts with "G-" to know it's a Google Analytics callback.
      if (containerId?.startsWith('G-')) {
        // This fires when the GA event has successfully been recorded.
        // We remove our fallback timer and call the callback function if one exists
        clearTimeout(eventCallbackFallback)
        callback && callback()
      }
    }
  }

  Object.entries(eventAttributes).forEach(([key, value]) => {
    // Following our implementation guide, keys are snake_cased and values are lowercased:
    // https://www.notion.so/GA4-Send-events-into-the-dataLayer-9f4ffbd7f6d5404595a87c4bc479842f#006f799eb5f54482b56ee199d8e54477
    eventData[snakeCase(key)] = normalizeAttributeValue(value)
  })

  // Clear any previous eCommerce data from the dataLayer
  if (isECommerceEvent) googleAnalytics({ ecommerce: null })

  // Push the GA event into Google Tag Manager:
  googleAnalytics(eventData)
}

// Standardised Google Analytics helpers https://github.com/envato/standard-analytics
const sendStandardEvent = ({
  elementId,
  elementLabel,
  elementPosition,
  eventType,
  pageType,
  itemType,
  sectionId,
  sectionLabel,
  tagName
}) => {
  googleAnalytics3Event({
    eventCategory: [sectionId, sectionLabel].filter(Boolean).join(';'),
    eventAction: [eventType, tagName].filter(Boolean).join(';'),
    eventLabel: [elementId, elementLabel].filter(Boolean).join(';'),
    eventValue: elementPosition,
    pageType: pageType,
    itemType: itemType
  })
}

const normalizeAttributeValue = (value) => {
  if (value === undefined) return undefined
  if (value === null) return undefined

  const normalizedValue = value.toString().toLowerCase().trim()

  if (normalizedValue === '') return undefined
  return normalizedValue
}

export { googleAnalytics3, googleAnalytics3Event, googleAnalyticsEvent, sendStandardEvent }
